// M5.10: centered modal showing the raw upstream payload for an
// ingestor event. Used by the player dashboard's "View raw"
// button on each row in the Recent events feed.
//
// Why: when a scoring rule doesn't fire ("scored but no awards"),
// the FIRST thing to check is what shape the upstream payload
// actually had. The normalised `payload` is what rules read, but
// fields the rule author didn't anticipate are dropped during
// normalisation; `raw` is the full untransformed upstream
// response (full Jira API response, full GitHub PR object, etc).
// Surfacing both side by side lets the rule author verify the
// upstream is sending what they expect AND check the normalisation
// isn't stripping a field they need.
//
// TTL: raw is nulled out by apps/api/src/jobs/raw-ttl.ts after
// INGESTOR_RAW_TTL_HOURS (default 24h). When raw is null the
// modal shows "(cleared after TTL)" with a note pointing at the
// normalised payload (which is preserved indefinitely) as the
// only thing available for older events.
//
// Security: ingestors strip tokens before persisting, so the raw
// payload never contains API keys. The full third-party API shape
// is still revealed; the modal only opens from rows the user
// already sees on the player dashboard (so they already have
// access to that data via the normalised payload view), and the
// admin endpoint for cross-user inspection is gated by
// requireRole('admin'). Right shape for the trust model.

import { useEffect, useMemo, useState } from 'react';

export interface RawEventData {
  id: string;
  type: string;
  payload: Record<string, unknown>;
  raw: Record<string, unknown> | null;
  ingestor_name?: string;
  ingestor_type?: string;
  actor_external: string | null;
  target_external: string | null;
  occurred_at: string;
  scored_at: string | null;
}

interface RawEventModalProps {
  event: RawEventData;
  onClose: () => void;
}

export function RawEventModal({ event, onClose }: RawEventModalProps) {
  // ESC closes (matches the coin-chip + grant modals).
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-50 grid place-items-center bg-black/40 p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="raw-event-title"
      onClick={onClose}
    >
      <div
        className="card flex max-h-[85vh] w-full max-w-3xl flex-col p-4"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="mb-3 flex items-start justify-between gap-2">
          <div>
            <h2 id="raw-event-title" className="text-lg font-semibold">
              Raw event
            </h2>
            <p className="text-xs text-ink-muted">
              {event.ingestor_name ?? event.ingestor_type ?? 'ingestor'} · {event.type} ·{' '}
              {new Date(event.occurred_at).toLocaleString()}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="btn-ghost"
            aria-label="Close"
          >
            ✕
          </button>
        </header>
        <RawEventBody event={event} />
      </div>
    </div>
  );
}

function RawEventBody({ event }: { event: RawEventData }) {
  // Two tabs: raw (the full upstream response) and payload
  // (the normalised shape the scoring engine reads). The
  // payload tab is always available; the raw tab may be empty
  // if the TTL job has run.
  const [tab, setTab] = useState<'raw' | 'payload'>('raw');
  const rawJson = useMemo(
    () => (event.raw ? JSON.stringify(event.raw, null, 2) : null),
    [event.raw],
  );
  const payloadJson = useMemo(
    () => JSON.stringify(event.payload, null, 2),
    [event.payload],
  );
  const activeJson = tab === 'raw' ? rawJson : payloadJson;
  const activeLabel = tab;
  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="mb-2 flex items-center gap-1 text-xs">
        <button
          type="button"
          className={
            'rounded-md px-2 py-1 ' +
            (tab === 'raw'
              ? 'bg-bg-elevated text-ink'
              : 'text-ink-muted hover:text-ink')
          }
          onClick={() => setTab('raw')}
        >
          Raw
        </button>
        <button
          type="button"
          className={
            'rounded-md px-2 py-1 ' +
            (tab === 'payload'
              ? 'bg-bg-elevated text-ink'
              : 'text-ink-muted hover:text-ink')
          }
          onClick={() => setTab('payload')}
        >
          Payload (normalised)
        </button>
        <div className="ml-auto flex items-center gap-2">
          {event.scored_at ? (
            <span className="rounded bg-bg-elevated px-2 py-0.5 text-ink-muted">
              scored
            </span>
          ) : (
            <span className="rounded bg-accent-warn/15 px-2 py-0.5 text-accent-warn">
              unscored
            </span>
          )}
          {tab === 'raw' && !event.raw ? (
            <span className="rounded bg-ink-dim/20 px-2 py-0.5 text-ink-muted">
              cleared after TTL
            </span>
          ) : activeJson ? (
            <span className="rounded bg-bg-elevated px-2 py-0.5 text-ink-muted">
              {formatBytes(activeJson.length * 2)}
            </span>
          ) : null}
        </div>
      </div>
      {tab === 'raw' && !event.raw ? (
        <p className="rounded-md border border-ink-dim/40 bg-bg-elevated p-4 text-sm text-ink-muted">
          This event was received more than{' '}
          <code>INGESTOR_RAW_TTL_HOURS</code> ago and the raw payload has
          been cleared from storage. The normalised payload (see the
          &quot;Payload&quot; tab) is preserved indefinitely.
        </p>
      ) : (
        <div className="flex min-h-0 flex-1 flex-col">
          <pre className="flex-1 overflow-auto rounded-md border border-white/5 bg-bg-elevated p-3 font-mono text-xs leading-relaxed text-ink">
            {activeJson}
          </pre>
          <footer className="mt-2 flex justify-end gap-2 text-xs">
            <button
              type="button"
              className="btn-ghost"
              onClick={async () => {
                if (!activeJson) return;
                try {
                  await navigator.clipboard.writeText(activeJson);
                } catch {
                  // Clipboard write can fail in non-secure
                  // contexts or with strict permission policies.
                  // The user can still select-and-copy from
                  // the <pre> element manually.
                }
              }}
            >
              Copy
            </button>
            <button
              type="button"
              className="btn"
              onClick={() => {
                if (!activeJson) return;
                const blob = new Blob([activeJson], {
                  type: 'application/json',
                });
                const url = URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = `event-${event.id}-${activeLabel}.json`;
                document.body.appendChild(a);
                a.click();
                a.remove();
                URL.revokeObjectURL(url);
              }}
            >
              Download .json
            </button>
          </footer>
        </div>
      )}
    </div>
  );
}

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}
